// Report («Отчёт») — spec §6.5 (v2.5): the engine's deterministic report
// for the psychologist handoff. GET /engine/report (no LLM, no quotes);
// 429 = cooldown with retry_at; 409 = not engine-enabled yet. The consent
// to the transfer is asked by the BOT in the conversation — the screen
// only displays its state. «Записаться» opens the Chatwoot chat.

import type { ReactNode } from 'react'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { reportApi } from '../../api/report'
import { chatwootApi } from '../../api/chatwoot'
import { useChatwoot } from '../../hooks/useChatwoot'
import type { EngineReport } from '../../types/api'
import SessionPopup from '../Psychologist/SessionPopup'
import styles from './Report.module.css'

const TREND_LABELS: Record<string, string> = {
  improving: 'становится легче',
  worsening: 'тяжелее',
  flat: 'без выраженной динамики',
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className={styles.statCell}>
      <div className={styles.statValue}>{value}</div>
      <div className={styles.statLabel}>{label}</div>
    </div>
  )
}

function trendText(report: EngineReport): string {
  const dyn = (report.dynamics || {}) as Record<string, unknown>
  if (typeof dyn.intensity_trend === 'number' && dyn.intensity_trend < 0) return TREND_LABELS.improving
  if (typeof dyn.intensity_trend === 'number' && dyn.intensity_trend > 0) return TREND_LABELS.worsening
  return String(dyn.phase ?? TREND_LABELS.flat)
}

export default function Report() {
  const navigate = useNavigate()
  const { open: openChat } = useChatwoot()
  const [state, setState] = useState<'loading' | 'ready' | 'cooldown' | 'no-subscription' | 'error'>('loading')
  const [report, setReport] = useState<EngineReport | null>(null)
  const [retryAt, setRetryAt] = useState<string | null>(null)
  const [popup, setPopup] = useState(false)

  const load = useCallback(() => {
    setState('loading')
    return reportApi
      .engine()
      .then((data) => {
        setReport(data)
        setState('ready')
      })
      .catch((err) => {
        if (err.status === 429) {
          const detail = err.body?.detail
          const ra = typeof detail === 'object' && detail && 'retry_at' in detail
            ? String((detail as { retry_at?: string }).retry_at)
            : null
          setRetryAt(ra)
          setState('cooldown')
        } else if (err.status === 409) {
          setState('no-subscription')
        } else {
          setState('error')
        }
      })
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const bookSession = async () => {
    setPopup(false)
    try {
      await chatwootApi.sendMessage({ message: 'Здравствуйте! Хочу записаться на подарочную консультацию' })
    } catch {
      /* widget opens regardless */
    }
    openChat()
  }

  if (state === 'loading') return <div className={styles.wrap}><div className={styles.centered}><div className={styles.spinner} /><p>Загружаем отчёт…</p></div></div>
  if (state === 'no-subscription') {
    return (
      <div className={styles.wrap}>
        <div className={styles.centered}>
          <p className={styles.emptyTitle}>Отчёт появится после подписки</p>
          <p>Отчёт строится по вашим беседам с ботом — первая уже создаст его.</p>
        </div>
      </div>
    )
  }
  if (state === 'cooldown') {
    return (
      <div className={styles.wrap}>
        <div className={styles.centered}>
          <p className={styles.emptyTitle}>Отчёт недавно сформирован</p>
          <p>
            Следующее обновление доступно{retryAt ? ` с ${new Date(retryAt).toLocaleString('ru-RU')}` : ' через 3 дня'}.
          </p>
        </div>
      </div>
    )
  }
  if (state === 'error') {
    return (
      <div className={styles.wrap}>
        <div className={styles.centered}>
          <p>Не удалось загрузить отчёт.</p>
          <button className={styles.ghostBtn} onClick={load}>Повторить</button>
        </div>
      </div>
    )
  }

  const r = report
  if (!r) return null
  const dyn = (r.dynamics || {}) as Record<string, unknown>
  const goals = Array.isArray(r.goals) ? r.goals : []
  const tried = Array.isArray(r.tried) ? r.tried : []
  const hyps = Array.isArray(r.hypotheses) ? r.hypotheses : []
  const distortions = Array.isArray(r.distortions) ? r.distortions : []
  const flags = Array.isArray(r.risk_flags) ? r.risk_flags : []
  const handoff = (r.handoff || {}) as { consent?: boolean }

  return (
    <div className={styles.wrap}>
      <h1 className={styles.pageTitle}>Отчёт</h1>
      <button className={styles.backBtn} onClick={() => navigate('/psychologist')}>← Назад в «Психолог»</button>

      {typeof r.request === 'string' && r.request && (
        <div className={styles.sessionQuestion}>
          <span className={styles.sqLabel}>Вопрос на сессию:</span>
          <p>{r.request}</p>
        </div>
      )}

      <section className={styles.block}>
        <h3>Динамика</h3>
        <div className={styles.statGrid}>
          <Row label="Динамика состояния" value={trendText(r)} />
          <Row label="Сессий" value={String(dyn.sessions_total ?? '—')} />
          <Row label="Часов в диалоге" value={String(dyn.hours_total ?? '—')} />
        </div>
      </section>

      <section className={styles.block}>
        <h3>Цели</h3>
        {goals.length ? (
          goals.map((g) => {
            const goal = g as Record<string, unknown>
            return (
              <p key={String(goal.id ?? '')} className={styles.quoteText}>
                {String(goal.text ?? '')} — {String(goal.status ?? '')}
              </p>
            )
          })
        ) : (
          <p className={styles.emptyInline}>Целей пока нет.</p>
        )}
      </section>

      <section className={styles.block}>
        <h3>Что пробовали</h3>
        {tried.length ? (
          <div className={styles.techRow}>
            {tried.map((t, i) => {
              const entry = t as Record<string, unknown>
              return (
                <span key={i} className={styles.tech}>
                  {String(entry.skill ?? '')} <em>×{entry.outcome === 'completed' ? 'завершено' : String(entry.outcome ?? '')}</em>
                </span>
              )
            })}
          </div>
        ) : (
          <p className={styles.emptyInline}>Практики не зафиксированы.</p>
        )}
      </section>

      <section className={styles.block}>
        <h3>Гипотезы</h3>
        {hyps.length ? (
          hyps.map((h) => {
            const hyp = h as Record<string, unknown>
            return <p key={String(hyp.id ?? '')} className={styles.quoteText}>{String(hyp.text ?? '')}</p>
          })
        ) : (
          <p className={styles.emptyInline}>Гипотез в работе нет.</p>
        )}
      </section>

      {distortions.length > 0 && (
        <section className={styles.block}>
          <h3>Искажения</h3>
          {distortions.map((d, i) => {
            const dist = d as Record<string, unknown>
            return (
              <p key={i} className={styles.quoteText}>
                {String(dist.type ?? '')} × {String(dist.count ?? '')}
              </p>
            )
          })}
        </section>
      )}

      {flags.length > 0 && (
        <section className={styles.block}>
          <h3>Флаги риска</h3>
          {flags.map((f, i) => {
            const flag = f as Record<string, unknown>
            return (
              <p key={i} className={styles.quoteText}>
                {String(flag.type ?? '')} — {String(flag.level ?? '')}
              </p>
            )
          })}
        </section>
      )}

      <section className={styles.block}>
        <div className={styles.blockHeader}>
          <h3>Передача психологу</h3>
        </div>
        <p>
          {handoff.consent
            ? 'Согласие на передачу дано — психолог получит отчёт и контекст до встречи.'
            : 'Передача отключена. Бот спросит согласие в разговоре — оно отдельное от согласия на обработку данных.'}
        </p>
      </section>

      <div className={styles.foot}>
        <button className={styles.primaryBtn} onClick={() => setPopup(true)}>
          Записаться на консультацию
        </button>
      </div>

      {popup && (
        <SessionPopup
          badge
          text="Психолог изучит вашу историю до встречи, и вы начнёте сразу с сути."
          onBook={bookSession}
          onClose={() => setPopup(false)}
        />
      )}
    </div>
  )
}
